'use client';
import { useEffect, useRef, useState } from 'react';
import SearchIcon from 'components/_other/icons/SearchIcon';
import SearchResults from '@/components/header/search/SearchResults';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/app/i18n/client';
import Link from 'next/link';
import CloseIcon from '@/components/_other/icons/CloseIcon';
import Button from '@/components/_other/button/Button';

const Search = () => {
  const { t } = useTranslation();
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const router = useRouter();

  const handleSearchValueChange = debounce(value => setSearchTerm(value), 500);

  const handleEnterPress = (key, value) => {
    if (key === 'Enter' && value !== '') {
      router.push(`/vyhladavanie/${encodeURIComponent(value)}`);
      closeSearch();
    }
  };

  const handleButtonClick = () => {
    if (inputRef.current.value !== '') {
      router.push(`/vyhladavanie/${encodeURIComponent(inputRef.current.value)}`);
      closeSearch();
    }
  };

  const handleInputFocus = () => {
    if (!showResults && searchTerm !== '') {
      setShowResults(true);
    }
  };

  const handleClickOutside = e => {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
      clearSearch();
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    inputRef.current.value = '';
    setShowResults(false);
  };
  const openSearch = () => {
    setIsSearchVisible(true);
  };

  const closeSearch = () => {
    clearSearch();
    setIsSearchVisible(false);
  };

  useEffect(() => {
    if (!showResults && searchTerm !== '') {
      setShowResults(true);
      return;
    }

    if (showResults && searchTerm === '') {
      setShowResults(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputRef]);

  // no scroll body if search is open
  useEffect(() => {
    const body = document.getElementsByTagName('body')[0];
    if (isSearchVisible) {
      body.classList.add('noScroll');
    } else {
      body.classList.remove('noScroll');
    }

    return () => {
      body.classList.remove('noScroll');
    };
  }, [isSearchVisible]);

  return (
    <>
      <Link href="#" onClick={openSearch}>
        <SearchIcon className={'dark:text-beige cursor-pointer'} />
      </Link>
      <div
        className={`fixed overflow-auto z-30 left-0 top-0 bottom-0 right-0 w-full bg-black bg-opacity-95 !m-0 !p-0 ${isSearchVisible ? 'flex' : 'hidden'}`}
        ref={wrapperRef}>
        <Link href="#" className="absolute top-6 right-8 lg:top-12 lg:right-12 text-white z-20" onClick={closeSearch}><CloseIcon /></Link>
        <div className="container relative py-24 lg:py-48">
          <div className="relative flex items-center border-b border-white">
            <input
              placeholder={t('search.placeholder')}
              onChange={e => handleSearchValueChange(e.currentTarget.value)}
              onKeyPress={e => handleEnterPress(e.key, e.target.value)}
              onFocus={handleInputFocus}
              ref={inputRef}
              className="bg-transparent w-full text-3xl pb-4 !text-white b-none focus:!outline-none focus:!ring-0"
            />
            <Button htmlType="submit" className="absolute right-0 !bg-transparent !px-5"
                    onClick={handleButtonClick}>{t('search.button_text')}</Button>
          </div>

          {showResults && (<SearchResults term={searchTerm} clearSearch={closeSearch} />)}
        </div>
      </div>
    </>
  );
};

export default Search;
